import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FarmDeliveries as FarmDelivery } from 'types/FarmData';
import { OpenHoursInput, normalizeOpenHours } from 'utils/timeHelpers';
import { parsePoint } from 'utils/geoHelpers';
import FarmPlaceModal, { FarmPlaceDetails } from './FarmPlaceModal';
import {
  RowIcon,
  SectionIcon,
  SectionNote,
  SectionToolbar,
  ViewButton,
} from './FarmSection';

type DeliveryCategory = 'pickup' | 'locker' | 'courier';
type DeliveryFilter = 'all' | DeliveryCategory;

const PICKUP_TYPE = 'self';

const CATEGORY_ORDER: DeliveryCategory[] = ['pickup', 'locker', 'courier'];

const CATEGORY_BY_TYPE: Record<string, DeliveryCategory> = {
  self: 'pickup',
  omniva: 'locker',
  dpd: 'locker',
  venipak: 'locker',
  izipizi: 'locker',
  delivery: 'courier',
  parcel: 'courier',
};

const CARRIER_NAMES: Record<string, string> = {
  omniva: 'Omniva',
  dpd: 'DPD',
  venipak: 'Venipak',
  izipizi: 'Izipizi',
};

const ICON_BY_CATEGORY: Record<DeliveryCategory, string> = {
  pickup: 'farm-place-farm',
  locker: 'farm-delivery-locker',
  courier: 'farm-delivery-courier',
};

const FILTER_LABEL: Record<DeliveryCategory, string> = {
  pickup: 'farm_view.filter_pickup',
  locker: 'farm_view.filter_lockers',
  courier: 'farm_view.filter_courier',
};

const TAG_LABEL: Record<DeliveryCategory, string> = {
  pickup: 'farm_view.delivery_tag_pickup',
  locker: 'farm_view.delivery_tag_locker',
  courier: 'farm_view.delivery_tag_courier',
};

interface FarmDeliveriesProps {
  deliveries: FarmDelivery[];
  farmName?: string;
  phone?: string | null;
  email?: string | null;
}

interface DeliveryRow {
  key: string;
  type: string;
  category?: DeliveryCategory;
  entries: FarmDelivery[];
  minPrice: number;
  hasPriceRange: boolean;
}

const FarmDeliveries = ({
  deliveries,
  farmName,
  phone,
  email,
}: FarmDeliveriesProps) => {
  const intl = useIntl();
  const [filter, setFilter] = useState<DeliveryFilter>('all');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const rows = useMemo<DeliveryRow[]>(() => {
    const groups = new Map<string, FarmDelivery[]>();

    deliveries.forEach((delivery) => {
      const type = delivery.type ?? '';
      const key = type === PICKUP_TYPE ? `${type}-${delivery.id}` : type;
      groups.set(key, [...(groups.get(key) ?? []), delivery]);
    });

    const rank = (category?: DeliveryCategory) =>
      category ? CATEGORY_ORDER.indexOf(category) : CATEGORY_ORDER.length;

    return Array.from(groups, ([key, entries]) => {
      const type = entries[0]?.type ?? '';
      const prices = entries.map((entry) => entry.price ?? 0);

      return {
        key,
        type,
        category: CATEGORY_BY_TYPE[type],
        entries,
        minPrice: Math.min(...prices),
        hasPriceRange: new Set(prices).size > 1,
      };
    }).sort((a, b) => rank(a.category) - rank(b.category));
  }, [deliveries]);

  const categories = CATEGORY_ORDER.filter((category) =>
    rows.some((row) => row.category === category)
  );

  const visible =
    filter === 'all' ? rows : rows.filter((row) => row.category === filter);

  const addressOf = (row: DeliveryRow) =>
    row.category === 'pickup' ? (row.entries[0]?.address?.trim() ?? '') : '';

  const titleOf = (row: DeliveryRow) => {
    if (row.category === 'locker' && CARRIER_NAMES[row.type]) {
      return CARRIER_NAMES[row.type];
    }

    if (row.category === 'pickup') {
      return intl.formatMessage({ id: 'delivery.self' });
    }

    if (row.category === 'courier') {
      return intl.formatMessage({ id: 'delivery.courier' });
    }

    const key = `delivery.${row.type}`;
    return key in intl.messages
      ? intl.formatMessage({ id: key })
      : (row.entries[0]?.name ?? row.type);
  };

  const whereOf = (row: DeliveryRow): { main?: string; sub?: string } => {
    if (row.category === 'locker') {
      return {
        main: intl.formatMessage({ id: 'farm_view.delivery_where_locker' }),
      };
    }

    if (row.category === 'courier') {
      return {
        main: intl.formatMessage({ id: 'farm_view.delivery_where_courier' }),
      };
    }

    if (row.category !== 'pickup') return {};

    const name = row.entries[0]?.name?.trim();
    const address = addressOf(row);

    return address ? { main: name, sub: address } : { main: name };
  };

  const priceOf = (row: DeliveryRow) => {
    if (row.minPrice <= 0 && !row.hasPriceRange) {
      return (
        <span className={'farm-row-price free'}>
          {intl.formatMessage({ id: 'farm_view.delivery_free' })}
        </span>
      );
    }

    const price = intl.formatNumber(row.minPrice, {
      style: 'currency',
      currency: 'EUR',
    });

    return (
      <span className={'farm-row-price'}>
        {row.hasPriceRange
          ? intl.formatMessage({ id: 'farm_view.price_from' }, { price })
          : price}
      </span>
    );
  };

  const selected = rows.find((row) => row.key === selectedKey);
  const selectedEntry = selected?.entries[0];
  const selectedDetails: FarmPlaceDetails | null =
    selected && selectedEntry
      ? {
          key: selected.key,
          title: selectedEntry.name?.trim() || titleOf(selected),
          subtitle: farmName,
          tag: intl.formatMessage({ id: 'delivery.self' }),
          tagIcon: ICON_BY_CATEGORY.pickup,
          point: parsePoint(selectedEntry.gps),
          address: addressOf(selected),
          phone: selectedEntry.phone || phone,
          email,
          days: normalizeOpenHours(selectedEntry.open_hours as OpenHoursInput),
          note: selectedEntry.description,
        }
      : null;

  return (
    <div className={'farm-section farm-deliveries'}>
      <SectionToolbar<DeliveryFilter>
        title={intl.formatMessage({ id: 'farm_view.deliveries_title' })}
        count={rows.length}
        filters={[
          {
            key: 'all',
            label: intl.formatMessage({
              id: 'farm_view.filter_all_deliveries',
            }),
          },
          ...categories.map((category) => ({
            key: category,
            label: intl.formatMessage({ id: FILTER_LABEL[category] }),
          })),
        ]}
        activeFilter={filter}
        onFilterChange={setFilter}
      />

      <div className={'farm-table'}>
        <div className={'farm-table-head'}>
          <span className={'farm-col-place'}>
            {intl.formatMessage({ id: 'farm_view.col_delivery' })}
          </span>
          <span className={'farm-col-where'}>
            {intl.formatMessage({ id: 'farm_view.col_where' })}
          </span>
          <span className={'farm-col-price'}>
            {intl.formatMessage({ id: 'farm_view.col_price' })}
          </span>
          <span className={'farm-col-action'} />
        </div>

        {visible.map((row) => {
          const isSelected = row.key === selectedKey;
          const where = whereOf(row);

          return (
            <div
              key={row.key}
              className={`farm-table-row${isSelected ? ' selected' : ''}`}
            >
              <div className={'farm-row-top'}>
                <div className={'farm-col-place'}>
                  <SectionIcon
                    name={ICON_BY_CATEGORY[row.category ?? 'courier']}
                  />
                  <span className={'farm-row-title'}>
                    <span className={'farm-row-name'}>{titleOf(row)}</span>
                    {row.category && (
                      <span className={'farm-row-tag'}>
                        {intl.formatMessage({ id: TAG_LABEL[row.category] })}
                      </span>
                    )}
                  </span>
                </div>
                <div className={'farm-col-price'}>{priceOf(row)}</div>
              </div>
              <div className={'farm-row-footer'}>
                <div className={'farm-col-where'}>
                  <RowIcon name={'farm-pin'} />
                  <span className={'farm-row-where'}>
                    {!!where.main && (
                      <span
                        className={`farm-row-where-main${where.sub ? ' has-sub' : ''}`}
                      >
                        {where.main}
                      </span>
                    )}
                    {!!where.sub && (
                      <span className={'farm-row-where-sub'}>{where.sub}</span>
                    )}
                  </span>
                </div>
                <div className={'farm-col-action'}>
                  {!!addressOf(row) && (
                    <ViewButton
                      active={isSelected}
                      onClick={() =>
                        setSelectedKey(isSelected ? null : row.key)
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <FarmPlaceModal
        place={selectedDetails}
        onClose={() => setSelectedKey(null)}
      />

      <SectionNote>
        {intl.formatMessage({ id: 'farm_view.deliveries_note' })}
      </SectionNote>
    </div>
  );
};

export default FarmDeliveries;
